import {
    Context
} from 'shared/context/GlobalState';
import {
    createContext, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import useFileHandler, {
    META_MAP_DIR
} from '../hooks/useFileHandler';
import useKBTableHandler from '../hooks/useKBTableHandler';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    FileManagerContext
} from 'shared/context/FileManagerContext';
import {
    kbService
} from 'shared/api/services/PROJECT-O/KnowledgeBuilder.service';
import {
    SubOrgContext
} from 'shared/context/SubOrganizationContext';

/**
 * @typedef {Object} KBContextType
 * @property {boolean} pageIsLoading The overall loading state of the Knowledge Builder.
 * @property {Map | null} selectedDocument The overall loading state of the Knowledge Builder.
 * @property {Map | null} selectedFolder The overall loading state of the Knowledge Builder.
 * @property {function(boolean): void} setPageIsLoading Function to set the loading state of Knowledge Builder.
 * @property {function(boolean): void} setSelectedDocument Function to set the selected document
 * @property {Map<string, import("../hooks/useFileHandler").KBDocument[]>} documentMap Map that contains the KB documents to be displayed.
 * @property {boolean} documentIsLoading The laoding state of the document list.
 * @property {function(string[]): void} fileMapHandler The laoding state of the document list.
 * @property {function(string): Map} objectSelector Selects the folder/file based on the object key.
 * @property {Object | null} collectionDetails Details of the collection from redis.
 * @property {boolean} collectionLoading Loading state of fetching the collection details.
 * @property {function(string): Map} toggleFolder Toggles the folder to open or close.
 * @property {function(Map, string):Object} handleMetadataChecker Returns the metadata of a KB Document.
 * @property {function(Map, string):Object} handleCheckMetadata Returns the metadata of a KB Document.
 * @property {function():void} handleAddFolder Add Folder to the file hierarchy
 * @property {Map} metaMap Returns the metadata of a KB Document.
 * @property {Object} tableHandler Table context data
 * @property {function(Map): Promise<void>} deleteObject Deletes the object in hierarchy
 * @property {function(Map, string): void} saveFolder Saves the new folder created
 * @property {function(): string} getCOSFormatDirectory Returns a string that is formatte for prefix in COS.
 * @property {string} bucketId The bucket ID of the current collection.
 * @property {Function} refreshList Refreshes the document list.
 * @property {Object} l1Status The state of L1.
 * @property {Function} setDocumentIsLoading Sets the loading state of document list
 * @property {function(string): string} getMemberNameByUserId Gets the name of the member by user ID.
 * @property {function(): void} loadCollectionDetails Loads the details of the collection
 * @property {function(): void} setProcessHistory Setter to list the process history of the collection
 * @property {Array} processHistory Setter to list the process history of the collection
 * @property {boolean} processHistoryLoading Process history loading state
 * @property {function(string): string} loadProcessHistory Loads the process history of the collection
 * @property {function(string): string[]} findFilesInFolder Finds the files to be deleted
 * @property {function(): void} loadDocuments loads the documents in the collection. Also handles the continuation token.
 * @property {import('react').RefObject} continuationToken The continuation token to load more documents.
 *
 */

/**
 * Context for managing global state in the Knowledge Builder component
 * @type {import('react').Context<KBContextType>}
 */
export const KBContext = createContext({
    selectedFolder: null,
    handleCheckMetadata: () => ({}),
    getMemberNameByUserId: () => '--',
    loadDocuments: () => {},
    continuationToken: {
        current: null,
    },
    uploadProgress: [],
    uploadDialogOpen: false,
    setUploadProgress: () => {},
    setUploadDialogOpen: () => {},
});

/**
 * Provider component for Knowledge Builder global context
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children Child components that will have access to the context
 * @returns {JSX.Element} Knowledge Builder context provider wrapper
 */
export function KBGlobalContextProvider({
    children
}) {
    /**
   * Outputs a formatted string as the bucket name/ID.
   */

    const {
        collections,
        selectedSubOrganization,
        collectionLoading,
        vectorIndex,
        fetchCollections,
    } = useContext(SubOrgContext);
    const {
        l1Status, l2Status, lastL1Message, lastL2Message
    } = useContext(FileManagerContext);
    // Previous collection-level L1 status — used to detect when a run finishes
    // so we can reconcile per-file rows that were optimistically set to
    // "processing" and would otherwise spin forever.
    const prevL1StatusRef = useRef(l1Status);
    const {
        selectedOrganization
    } = useContext(OrganizationContext);
    const {
        oragID, subOragID, collectionId
    } = useParams();

    const loadProcessHistory = async (organizationId, collectionId) => {
        setProcessHistoryLoading(true);
        try {
            const processHistory = await kbService.getCollectionProcessHistory(
                organizationId,
                collectionId
            );
            if (processHistory && Array.isArray(processHistory.data)) {
                setProcessHistory(
                    processHistory.data.sort(
                        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
                    )
                );
            } else {
                setProcessHistory([]);
            }
        } catch (error) {
            console.error('Error loading process history:', error);
            setProcessHistory([]);
        } finally {
            setProcessHistoryLoading(false);
        }
    };

    useEffect(() => {

        loadCollectionDetails();
    }, [collections, selectedSubOrganization, vectorIndex, selectedOrganization]);
    const loadCollectionDetails = () => {

        setCollectionDetails(collections.find((coll) => coll._id === collectionId));
    };

    const getBucketId = () => {
        return `coll-${subOragID}-${collectionId}`;
    };

    const getMemberNameByUserId = (userId) => {
        const memberName =
      selectedOrganization?.members.find((member) => member.id._id === userId)
          ?.id.name || '--';

        return memberName;
    };

    const [pageIsLoading, setPageIsLoading] = useState(false);
    const [collectionDetails, setCollectionDetails] = useState(null);
    const [processHistory, setProcessHistory] = useState([]);
    const [processHistoryLoading, setProcessHistoryLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState([]);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const bucketId = useMemo(
        () => getBucketId(),
        [oragID, subOragID, collectionId]
    );
    const {
        documentMap,
        documentIsLoading,
        selectedDocument,
        selectedFolder,
        metaMap,
        fileMapHandler,
        objectSelector,
        toggleFolder,
        handleMetadataChecker,
        handleCheckMetadata,
        handleAddFolder,
        deleteObject,
        saveFolder,
        refreshList,
        setDocumentIsLoading,
        findFilesInFolder,
        loadDocuments,
        updateFileStatus,
        ContinuationToken,
    } = useFileHandler(oragID, bucketId);

    // Global user data.
    const {
        user
    } = useContext(Context);

    const tableHandler = useKBTableHandler(selectedFolder, handleCheckMetadata);

    const getCOSFormatDirectory = () => {
    //
        const selectedFolderDir = selectedFolder.get(META_MAP_DIR);
        let cosDirectory = '';
        if (selectedFolderDir.includes('%__parentDir__%/')) {
            cosDirectory = selectedFolderDir.replace('%__parentDir__%/', '');
        } else if (selectedFolderDir.includes('%__parentDir__%')) {
            cosDirectory = selectedFolderDir.replace('%__parentDir__%', '');
        } else {
            cosDirectory = selectedFolderDir;
        }
        return cosDirectory;
    };

    useEffect(() => {
        if (lastL1Message) {

            if (lastL1Message.files && Array.isArray(lastL1Message.files)) {
                updateFileStatus(lastL1Message.files, lastL1Message.status, 'l1');
            } else if (lastL1Message.key) {
                updateFileStatus([lastL1Message.key], lastL1Message.status, 'l1');
            }
        }
    }, [lastL1Message]);

    useEffect(() => {
        if (lastL2Message) {

            if (lastL2Message.files && Array.isArray(lastL2Message.files)) {
                updateFileStatus(lastL2Message.files, lastL2Message.status, 'l2');
            } else if (lastL2Message.key) {
                updateFileStatus([lastL2Message.key], lastL2Message.status, 'l2');
            }
        }
    }, [lastL2Message]);

    // When an L1 run leaves "processing" (-> idle/completed/cancelled/error),
    // reload the document list so per-file rows reconcile to their real backend
    // status. Without this, a row optimistically set to "processing" stays
    // spinning even though the collection-level status shows the run is done.
    useEffect(() => {
        const was = prevL1StatusRef.current;
        prevL1StatusRef.current = l1Status;
        if (was === 'processing' && l1Status !== 'processing') {
            refreshList();
        }
    }, [l1Status]);

    useEffect(() => {
        fetchCollections();
    }, []);
    return (
        <>
            <KBContext.Provider
                value={{
                    // ==== State Setters ====
                    setPageIsLoading,
                    fileMapHandler,
                    toggleFolder,
                    objectSelector,
                    setDocumentIsLoading,
                    setProcessHistory,
                    setProcessHistoryLoading,
                    setUploadProgress,
                    setUploadDialogOpen,

                    // ==== States ====
                    pageIsLoading,
                    selectedDocument,
                    selectedFolder,
                    documentMap,
                    documentIsLoading,
                    user,
                    metaMap,
                    collectionDetails,
                    collectionLoading,
                    l1Status,
                    l2Status,
                    processHistory,
                    processHistoryLoading,
                    uploadProgress,
                    uploadDialogOpen,
                    // Utility
                    /**
           * @deprecated
           */
                    handleMetadataChecker,
                    handleCheckMetadata,
                    handleAddFolder,
                    loadCollectionDetails,
                    deleteObject,
                    saveFolder,
                    tableHandler,
                    getCOSFormatDirectory,
                    getMemberNameByUserId,
                    refreshList,
                    loadProcessHistory,
                    findFilesInFolder,
                    loadDocuments,
                    updateFileStatus,
                    continuationToken: ContinuationToken,
                    // Values
                    bucketId,
                }}
            >
                {children}
            </KBContext.Provider>
        </>
    );
}
